import React, { useEffect, useRef } from 'react';

/**
 * Reusable Drawer component for displaying forms in a slide-out panel using only Tailwind CSS
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the drawer is open
 * @param {Function} props.setIsOpen - Function to toggle the drawer state
 * @param {React.ReactNode} props.children - Content to display inside the drawer
 * @param {number} props.width - Width of the drawer in pixels (default: 600)
 */
const Drawer = ({ isOpen, setIsOpen, children, width = 600 }) => {
  const drawerRef = useRef(null);

  // Close drawer when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event) => {
      // Une fenêtre ouverte depuis le volet mais rattachée à la page (portail
      // React) n'est pas « en dehors » : y cliquer ne doit pas fermer le volet.
      if (event.target.closest?.('[data-keep-drawer-open]')) return;
      if (drawerRef.current && !drawerRef.current.contains(event.target) && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen, setIsOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      // Échap déjà pris en charge plus bas — une liste déroulante qui referme
      // son menu, par exemple : il ne doit pas emporter le volet et sa saisie.
      if (event.defaultPrevented) return;
      // Même règle que pour le clic : une fenêtre ouverte depuis le volet
      // n'est pas « en dehors ».
      if (event.target.closest?.('[data-keep-drawer-open]')) return;
      setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, setIsOpen]);

  // Prevent scrolling when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  return (
    <div className={`fixed inset-0 z-50 ${isOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
      {/* Overlay */}
      <div
        role="presentation"
        className={`absolute inset-0 bg-black transition-opacity duration-300 ease-in-out ${
          isOpen ? 'opacity-50' : 'opacity-0'
        }`}
        onClick={() => setIsOpen(false)}
      ></div>

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`absolute right-0 top-0 h-full bg-white shadow-xl transition-transform duration-500 ease-in-out transform ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        // Jamais plus large que l'écran : une largeur fixe en pixels débordait
        // sur téléphone.
        style={{ width: `${width}px`, maxWidth: '100vw' }}
      >
        <div className="flex flex-col h-full">
          
          <div className="flex-1 overflow-y-auto p-4">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default Drawer;